/* eslint-disable */
import { getLocale, experimentalStaticLocale } from '../runtime.js';

/** @typedef {import('../runtime.js').LocalizedString} LocalizedString */

/** @typedef {{ email: NonNullable<unknown>, dmca_agent: NonNullable<unknown> }} Legal_Content_BodyInputs */

const en_legal_content_body = /** @type {(inputs: Legal_Content_BodyInputs) => LocalizedString} */ (i) => {
	return /** @type {LocalizedString} */ (`This policy explains what may not be posted on Complete Athlete, how to report it and how we moderate. It meets the notice-and-action duties of the EU Digital Services Act and covers US copyright notices under the DMCA.

## 1. What is not allowed

Illegal content; scams and misleading offers; fake reviews or ratings; infringement of copyright, trademarks or privacy; harassment or hate; dangerous health claims; malware or spam.

## 2. How we moderate

People review reports and make these decisions. We do not use automated tools to decide on content. Depending on the case we may hide a comment, ban someone from commenting, hide or restrict a page, or suspend an account or part of one, such as selling or public booking. We act proportionately and with regard to everyone's fundamental rights, including freedom of expression.

## 3. Report illegal content

Write to ${i?.email}, or use the [contact form](/contact), and include:

- why you believe the content is illegal;
- its exact address;
- your name and email, unless the report concerns certain child sexual abuse offences;
- a statement that you believe your report is accurate and complete.

We confirm receipt, decide promptly and tell you the outcome and how to challenge it. We may stop handling reports from people who often send manifestly unfounded ones.

## 4. If we act against your content

We will tell you what we did and why, the facts and the rule or law we relied on, and how to challenge it. You can ask us to review the decision by writing to ${i?.email} within 6 months, and a person who was not involved in the original decision will look at it, free of charge. You can also go to court, or complain to the President of UKE, Poland's Digital Services Coordinator. Businesses should also see the suspension rules in their terms.

## 5. Copyright notices (United States)

If you believe content infringes your copyright, send our designated agent, ${i?.dmca_agent}, a notice with:

- your physical or electronic signature;
- the work you say is infringed;
- the content and where it is;
- your contact details;
- a statement of your good-faith belief that the use is not authorized;
- a statement, under penalty of perjury, that the information is accurate and that you are authorized to act.

**Counter-notice:** if your content was removed by mistake, send us your signature, the content and its former location, your contact details, a statement under penalty of perjury that you believe it was removed by mistake, and your consent to the jurisdiction of the federal court for your district (or, outside the US, any district where we may be found). We may restore it after 10 to 14 business days unless the claimant files suit. We close the accounts of repeat infringers where appropriate.

## 6. Authorities

Authorities can reach our single point of contact at ${i?.email}, in Polish or English.`)
};

const pl_legal_content_body = /** @type {(inputs: Legal_Content_BodyInputs) => LocalizedString} */ (i) => {
	return /** @type {LocalizedString} */ (`Niniejsza polityka wyjaśnia, czego nie wolno publikować w Complete Athlete, jak to zgłaszać i jak moderujemy treści. Realizuje obowiązki dotyczące mechanizmu zgłaszania i działania wynikające z unijnego aktu o usługach cyfrowych i obejmuje amerykańskie zgłoszenia naruszeń praw autorskich na podstawie DMCA.

## 1. Co jest niedozwolone

Treści nielegalne; oszustwa i oferty wprowadzające w błąd; fałszywe opinie lub oceny; naruszanie praw autorskich, znaków towarowych lub prywatności; nękanie lub nienawiść; niebezpieczne twierdzenia dotyczące zdrowia; złośliwe oprogramowanie lub spam.

## 2. Jak moderujemy

Zgłoszenia sprawdzają ludzie i to oni podejmują decyzje. Nie używamy zautomatyzowanych narzędzi do podejmowania decyzji o treściach. W zależności od sprawy możemy ukryć komentarz, zablokować komuś możliwość komentowania, ukryć lub ograniczyć stronę albo zawiesić konto lub jego część, na przykład sprzedaż lub publiczne rezerwacje. Działamy proporcjonalnie i z poszanowaniem praw podstawowych wszystkich osób, w tym wolności wypowiedzi.

## 3. Zgłaszanie nielegalnych treści

Napisz na adres ${i?.email} lub skorzystaj z [formularza kontaktowego](/contact) i podaj:

- dlaczego uważasz, że treść jest nielegalna;
- jej dokładny adres;
- swoje imię i nazwisko oraz adres e-mail, chyba że zgłoszenie dotyczy niektórych przestępstw związanych z seksualnym wykorzystywaniem dzieci;
- oświadczenie, że w Twoim przekonaniu zgłoszenie jest prawidłowe i kompletne.

Potwierdzamy otrzymanie zgłoszenia, niezwłocznie podejmujemy decyzję i informujemy Cię o jej wyniku oraz o tym, jak można ją zakwestionować. Możemy przestać rozpatrywać zgłoszenia osób, które często wysyłają zgłoszenia oczywiście bezzasadne.

## 4. Jeśli podejmiemy działania wobec Twoich treści

Poinformujemy Cię, co zrobiliśmy i dlaczego, na jakich faktach i na jakiej zasadzie lub przepisie się oparliśmy oraz jak możesz zakwestionować tę decyzję. Możesz poprosić o jej ponowne rozpatrzenie, pisząc na adres ${i?.email} w ciągu 6 miesięcy, a sprawdzi ją bezpłatnie osoba, która nie brała udziału w pierwotnej decyzji. Możesz też wystąpić do sądu lub złożyć skargę do Prezesa UKE, polskiego Koordynatora do spraw usług cyfrowych. Firmy powinny też zapoznać się z zasadami zawieszania w swoim regulaminie.

## 5. Zgłoszenia naruszeń praw autorskich (Stany Zjednoczone)

Jeśli uważasz, że treść narusza Twoje prawa autorskie, wyślij do naszego wyznaczonego pełnomocnika, ${i?.dmca_agent}, zgłoszenie zawierające:

- Twój podpis fizyczny lub elektroniczny;
- utwór, którego prawa według Ciebie zostały naruszone;
- daną treść i miejsce, w którym się znajduje;
- Twoje dane kontaktowe;
- oświadczenie, że w dobrej wierze uważasz, iż korzystanie z utworu nie jest dozwolone;
- oświadczenie, złożone pod rygorem odpowiedzialności za składanie fałszywych oświadczeń (under penalty of perjury), że informacje są prawdziwe i że masz upoważnienie do działania.

**Kontrzawiadomienie:** jeśli Twoja treść została usunięta przez pomyłkę, wyślij nam swój podpis, treść i jej poprzednią lokalizację, swoje dane kontaktowe, oświadczenie złożone pod rygorem odpowiedzialności za składanie fałszywych oświadczeń (under penalty of perjury), że Twoim zdaniem treść została usunięta przez pomyłkę, oraz zgodę na jurysdykcję sądu federalnego Twojego okręgu (a poza Stanami Zjednoczonymi: dowolnego okręgu, w którym można nas znaleźć). Możemy przywrócić treść po upływie od 10 do 14 dni roboczych, chyba że zgłaszający wniesie pozew. W uzasadnionych przypadkach zamykamy konta osób wielokrotnie naruszających prawa.

## 6. Organy

Organy mogą kontaktować się z naszym pojedynczym punktem kontaktowym pod adresem ${i?.email}, w języku polskim lub angielskim.`)
};

/**
* | output |
* | --- |
* | "This policy explains what may not be posted on Complete Athlete, how to report it and how we moderate. It meets the notice-and-action duties of the EU Digita..." |
*
* @param {Legal_Content_BodyInputs} inputs
* @param {{ locale?: "en" | "pl" }} options
* @returns {LocalizedString}
*/
export const legal_content_body = /** @type {((inputs: Legal_Content_BodyInputs, options?: { locale?: "en" | "pl" }) => LocalizedString) & import('../runtime.js').MessageMetadata<Legal_Content_BodyInputs, { locale?: "en" | "pl" }, {}>} */ ((inputs, options = {}) => {
	const locale = experimentalStaticLocale ?? options.locale ?? getLocale()
	if (locale === "pl") return pl_legal_content_body(inputs)
	return en_legal_content_body(inputs)
});